import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { CommonModule } from "@angular/common";
import { DxSelectBoxModule, DxTemplateModule } from "devextreme-angular";
import DataSource from "devextreme/data/data_source";
import { UserAvatarComponent } from "../user-avatar/user-avatar.component";
import { SvgIconComponent } from "../svg-icon/svg-icon.component";

interface HolderOption {
  key: string;
  kind: "user" | "department";
  label: string;
  email?: string;
  departmentId?: number;
  group: string;
}

/**
 * #4994: picks who holds something - one person or a whole department (every member). Bind
 * [(email)] and [(departmentId)]; choosing one clears the other.
 */
@Component({
  selector: "app-holder-select",
  imports: [CommonModule, DxSelectBoxModule, DxTemplateModule, UserAvatarComponent, SvgIconComponent],
  templateUrl: "./holder-select.component.html",
  styleUrl: "./holder-select.component.css",
})
export class HolderSelectComponent implements OnChanges {
  /** { emailAddress, displayName } */
  @Input() users: any[] = [];
  /** { id, name } */
  @Input() departments: any[] = [];
  @Input() email: string | null = null;
  @Input() departmentId: number | null = null;
  @Input() placeholder: string = $localize`:@@selectPersonOrDepartment:Select a person or department`;
  @Input() showClearButton = true;
  @Input() disabled = false;
  @Output() emailChange = new EventEmitter<string | null>();
  @Output() departmentIdChange = new EventEmitter<number | null>();
  /** fires once per user change with both values */
  @Output() holderChange = new EventEmitter<{ email: string | null; departmentId: number | null }>();

  dataSource: DataSource;
  selectedKey: string | null = null;

  private readonly peopleGroup = $localize`:@@people:People`;
  private readonly departmentsGroup = $localize`:@@departments:Departments`;

  ngOnChanges(changes: SimpleChanges): void {
    // only a new list rebuilds the data source; the value echoing back through [(email)] must not
    if (changes["users"] || changes["departments"] || !this.dataSource) this.buildDataSource();
    this.selectedKey =
      this.departmentId != null ? "d:" + this.departmentId : this.email ? "u:" + this.email.toLowerCase() : null;
  }

  private buildDataSource(): void {
    const options: HolderOption[] = [
      ...(this.users ?? []).map((u) => ({
        key: "u:" + (u.emailAddress ?? "").toLowerCase(),
        kind: "user" as const,
        label: u.displayName || u.emailAddress,
        email: u.emailAddress,
        group: this.peopleGroup,
      })),
      ...(this.departments ?? []).map((d) => ({
        key: "d:" + d.id,
        kind: "department" as const,
        label: d.name,
        departmentId: d.id,
        group: this.departmentsGroup,
      })),
    ];
    this.dataSource = new DataSource({ store: options, group: "group", paginate: false, searchExpr: "label" });
  }

  onValueChanged(e: any): void {
    if (!e.event && e.value === this.selectedKey) return; // programmatic echo
    this.selectedKey = e.value;
    const email = e.value?.startsWith("u:") ? this.findEmail(e.value) : null;
    const departmentId = e.value?.startsWith("d:") ? Number(e.value.slice(2)) : null;
    this.email = email;
    this.departmentId = departmentId;
    this.emailChange.emit(email);
    this.departmentIdChange.emit(departmentId);
    this.holderChange.emit({ email, departmentId });
  }

  private findEmail(key: string): string | null {
    // keep the address exactly as the user list spells it
    return (this.users ?? []).find((u) => "u:" + (u.emailAddress ?? "").toLowerCase() === key)?.emailAddress ?? null;
  }
}
